import { startOfDay } from 'date-fns';
import { useState } from 'react';
import { LuEllipsis, LuPlus } from 'react-icons/lu';

import { Account, AccountGroup, CashFlowConfig, GUID } from '@ndvr/portfolio-api';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EllipsisTooltip } from '@/components/ui/EllipsisTooltip';
import { formatCurrency, formatDate } from 'utils';
import { withdrawalAccountName, withdrawalTitle } from 'views/portfolio/policy-withdrawals';
import { CashflowStream } from 'views/portfolio/PortfolioPage/tabs/liquidity-planning/EditCashflowDialog';
import { convertCashFlowConfigToForm } from 'views/portfolio/PortfolioPage/utils/FormCashFlowConfigConverter';
import { frequencyTypeToLabel } from 'views/portfolio/PortfolioPage/utils/frequencyToLabel';

import {
    PolicyTable as Table,
    PolicyTableBody as TableBody,
    PolicyTableCell as TableCell,
    PolicyTableHead as TableHead,
    PolicyTableHeader as TableHeader,
    PolicyTableRow as TableRow,
} from '../PolicyTable';
import { LifestyleDraft } from './policy-lifestyle';
import { withdrawalFromForm } from './policy-withdrawal-draft';
import { PolicyCashFlowChart } from './PolicyCashFlowChart';
import { PolicyLifestyleProtectionCard } from './PolicyLifestyleProtectionCard';
import { PlanStat, PlanStats } from './PolicyPlanStats';
import { newWithdrawal, PolicyWithdrawalForm, primaryButton } from './PolicyWithdrawalForm';

interface PolicyWithdrawalsManageProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    accountGroupGUID: GUID<AccountGroup>;
    /** Encryption context for new titles, matching the liquidity planning tab. */
    globalAccountGUID: string;
    /**
     * The plan's upcoming withdrawals.
     */
    withdrawals: CashFlowConfig[];
    /** Every withdrawal, ended ones included, so the chart can show past months too. */
    allWithdrawals: CashFlowConfig[];
    /** The section's summary figures, shown at the top of the list. */
    stats: PlanStat[];
    lifestyle: LifestyleDraft;
    onLifestyleChange: (update: (current: LifestyleDraft) => LifestyleDraft) => void;
    accounts: Account[];
    /** Called with the new or updated withdrawal. Nothing is persisted here; the page collects it as a draft. */
    onSave: (config: CashFlowConfig) => void;
    onDelete: (cashFlowConfigGUID: GUID<CashFlowConfig>) => void;
}

interface WithdrawalRowProps {
    config: CashFlowConfig;
    accountName: string;
    onEdit: () => void;
    onDelete: () => void;
}

const WithdrawalRow = ({ config, accountName, onEdit, onDelete }: WithdrawalRowProps) => {
    const title = withdrawalTitle(config);
    return (
        <TableRow>
            <TableCell>
                <EllipsisTooltip className="font-medium">{title}</EllipsisTooltip>
            </TableCell>
            <TableCell className="text-cocoa-800 whitespace-nowrap">
                {formatDate(config.factorDateFrame.startDate, 'MMM d, yyyy')}
            </TableCell>
            <TableCell className="font-medium">{frequencyTypeToLabel(config.factorDateFrame.frequencyType)}</TableCell>
            <TableCell>{accountName}</TableCell>
            <TableCell className="text-right font-medium whitespace-nowrap tabular-nums">
                {formatCurrency(config.amount)}
            </TableCell>
            <TableCell className="text-right">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="size-8 rounded-lg text-[#5A4F4E]"
                            aria-label={`Actions for ${title}`}
                        >
                            <LuEllipsis className="size-5" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="z-[70]">
                        <DropdownMenuItem onSelect={onEdit}>Edit</DropdownMenuItem>
                        <DropdownMenuItem onSelect={onDelete}>Delete</DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </TableCell>
        </TableRow>
    );
};

export const PolicyWithdrawalsManage = ({
    open,
    onOpenChange,
    accountGroupGUID,
    globalAccountGUID,
    withdrawals,
    allWithdrawals,
    stats,
    lifestyle,
    onLifestyleChange,
    accounts,
    onSave,
    onDelete,
}: PolicyWithdrawalsManageProps) => {
    // The withdrawal open in the form; the list shows while this is unset.
    const [editing, setEditing] = useState<CashflowStream | undefined>();

    const close = (next: boolean) => {
        if (!next) setEditing(undefined);
        onOpenChange(next);
    };

    const submit = (value: CashflowStream) => {
        const existing = withdrawals.find((item) => item.cashFlowConfigGUID === value.id);
        onSave(withdrawalFromForm(value, globalAccountGUID, accountGroupGUID, existing));
        setEditing(undefined);
    };

    return (
        <Dialog open={open} onOpenChange={close}>
            <DialogContent className="flex h-[92vh] max-h-[850px] w-[95vw] max-w-[1100px] flex-col gap-0 overflow-hidden rounded-xl border-[#CECAC6] bg-white p-0 tracking-[-0.02em]">
                <DialogHeader className="border-b border-[#CECAC6] px-6 pt-5 pb-4">
                    <DialogTitle className="font-heading text-cocoa-1000 text-[22px] leading-[1.15] font-normal tracking-[-0.015em]">
                        {!editing ? 'Withdrawal Plan' : editing.id ? 'Edit withdrawal' : 'New withdrawal'}
                    </DialogTitle>
                    <DialogDescription className="sr-only">Manage scheduled withdrawals</DialogDescription>
                </DialogHeader>
                {!editing ? (
                    <>
                        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-7 pt-6 pb-14">
                            <div className="flex flex-wrap items-end justify-between gap-4">
                                <PlanStats
                                    stats={stats}
                                    className="gap-5 [&_dd]:mt-1 [&_dd]:text-lg [&_dd]:font-semibold [&_dt]:text-[13px] [&_dt]:text-[#8A7F7A] [&>div]:min-w-34"
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        className="h-9 rounded-lg border-[#E0DAD5] bg-white px-3.5 text-sm font-medium text-[#1F1A19] hover:bg-[#F7F5F3]"
                                        onClick={() => setEditing(newWithdrawal())}
                                    >
                                        <LuPlus aria-hidden="true" className="size-4" />
                                        Add
                                    </Button>
                                </div>
                            </div>
                            <PolicyCashFlowChart withdrawals={allWithdrawals} today={startOfDay(new Date())} />
                            <Table className="table-fixed">
                                <colgroup>
                                    <col className="w-[30%]" />
                                    <col className="w-[16%]" />
                                    <col className="w-[16%]" />
                                    <col className="w-[20%]" />
                                    <col className="w-[12%]" />
                                    <col className="w-[6%]" />
                                </colgroup>
                                <TableHeader>
                                    <TableRow>
                                        {['Purpose', 'Starts', 'Frequency', 'From account', 'Amount', ''].map(
                                            (label, index) => (
                                                <TableHead
                                                    key={index}
                                                    className={label === 'Amount' ? 'text-right' : undefined}
                                                >
                                                    {label}
                                                </TableHead>
                                            ),
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {withdrawals.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={6}
                                                className="text-cocoa-500 py-8 text-center"
                                                role="status"
                                            >
                                                Set up an automated withdrawal plan.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        withdrawals.map((config) => (
                                            <WithdrawalRow
                                                key={config.cashFlowConfigGUID}
                                                config={config}
                                                accountName={withdrawalAccountName(accounts, config.accountGUID)}
                                                onEdit={() => setEditing(convertCashFlowConfigToForm(config))}
                                                onDelete={() => onDelete(config.cashFlowConfigGUID)}
                                            />
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                            <div className="pt-2">
                                <PolicyLifestyleProtectionCard
                                    value={lifestyle}
                                    accounts={accounts}
                                    onChange={onLifestyleChange}
                                />
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-3 border-t border-[#CECAC6] bg-[#FCFBFA] px-6 py-3.5">
                            <p className="text-[12.5px] text-[#897E7B]">
                                Changes apply when you save the Investment Policy.
                            </p>
                            <Button className={primaryButton} onClick={() => close(false)}>
                                Done
                            </Button>
                        </div>
                    </>
                ) : (
                    <PolicyWithdrawalForm
                        value={editing}
                        accounts={accounts}
                        onSubmit={submit}
                        onCancel={() => setEditing(undefined)}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
};
